'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import type { PortfolioLightboxItem } from '../shared/sections/portfolio-lightbox';
import type { SubjectsById } from '../shared/subjects-by-id';

// The modal and comparison controls are downloaded only after a photo is opened.
const PortfolioLightbox = dynamic(
  () => import('../shared/sections/portfolio-lightbox').then((mod) => mod.PortfolioLightbox),
  {
    ssr: false,
    loading: () => (
      <p role="status" className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-box bg-base-100 p-4 text-base-content shadow-lg">
        Opening photo…
      </p>
    ),
  },
);

/**
 * One client island enhances the server-rendered photo links. Delegation keeps
 * the cards out of the client module graph and avoids a provider and a client
 * component per card. Enter activates the native link just like a pointer click;
 * modified clicks and browsers without dialog support keep normal navigation.
 * Before hydration the same links open the full image directly.
 */
export const PortfolioGallery: React.FC<{
  id: string;
  items: readonly PortfolioLightboxItem[];
  showQuotes: boolean;
  /** Each item's Subject attribute by id, for the lightbox (`subjectsById`). */
  subjects?: SubjectsById;
  children: React.ReactNode;
}> = ({ id, items, showQuotes, subjects, children }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [lightboxWanted, setLightboxWanted] = useState(false);

  const onPhotoClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') return;
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[data-portfolio-photo]');
    if (!(link instanceof HTMLAnchorElement)) return;
    const index = Number(link.dataset.portfolioPhoto);
    if (!Number.isInteger(index) || index < 0 || index >= items.length) return;

    event.preventDefault();
    // Safari does not focus pointer-clicked controls consistently. This also
    // gives the native dialog a reliable return target when it closes.
    link.focus();
    setLightboxWanted(true);
    setOpenIndex(index);
  };

  return (
    <div onClick={onPhotoClick}>
      {children}
      {lightboxWanted ? (
        <PortfolioLightbox
          id={id}
          items={items}
          openIndex={openIndex}
          onOpenIndexChange={setOpenIndex}
          showQuotes={showQuotes}
          subjects={subjects}
        />
      ) : null}
    </div>
  );
};
